import React from 'react';
import { Users, TrendingUp, Clock, Activity } from 'lucide-react';

const StoreOccupancy: React.FC = () => {
  const currentOccupancy = 127;
  const capacity = 200;
  const occupancyPercent = (currentOccupancy / capacity) * 100;

  const hourlyData = [
    { hour: '9 AM', count: 45, percent: 23 },
    { hour: '10 AM', count: 78, percent: 39 },
    { hour: '11 AM', count: 102, percent: 51 },
    { hour: '12 PM', count: 134, percent: 67 },
    { hour: '1 PM', count: 156, percent: 78 },
    { hour: '2 PM', count: 145, percent: 73 },
    { hour: '3 PM', count: 127, percent: 64 },
    { hour: '4 PM', count: 98, percent: 49 }
  ];

  const zones = [
    { name: 'Entry Zone', current: 45, capacity: 60, avgDwell: '3m' },
    { name: 'Main Floor', current: 62, capacity: 100, avgDwell: '12m' },
    { name: 'Fitting Rooms', current: 8, capacity: 12, avgDwell: '8m' },
    { name: 'Checkout Area', current: 12, capacity: 28, avgDwell: '5m' }
  ];

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Store Occupancy
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Real-time tracking of customer traffic and zone utilization
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <Users size={24} style={{ color: 'var(--sca-teal)' }} />
            <span className="text-xs px-2 py-1 rounded-full" style={{
              backgroundColor: 'var(--sca-badge-bg)',
              color: 'var(--sca-teal)'
            }}>
              Live
            </span>
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            {currentOccupancy}
          </div>
          <div className="text-sm mb-2" style={{ color: 'var(--sca-muted)' }}>
            Current Occupancy
          </div>
          <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
            {occupancyPercent.toFixed(0)}% of capacity ({capacity})
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <Clock size={24} style={{ color: 'var(--sca-teal)' }} />
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            18m
          </div>
          <div className="text-sm mb-2" style={{ color: 'var(--sca-muted)' }}>
            Average Dwell Time
          </div>
          <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
            +12% vs. yesterday
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <TrendingUp size={24} style={{ color: 'var(--sca-teal)' }} />
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            12-2 PM
          </div>
          <div className="text-sm mb-2" style={{ color: 'var(--sca-muted)' }}>
            Peak Hours
          </div>
          <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
            156 avg. customers
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Hourly Traffic Pattern
        </h3>
        <div className="space-y-3">
          {hourlyData.map((hour, idx) => (
            <div key={idx}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>{hour.hour}</span>
                <span className="text-sm font-semibold" style={{ color: 'var(--sca-teal)' }}>
                  {hour.count} customers ({hour.percent}%)
                </span>
              </div>
              <div className="w-full h-3 rounded-full" style={{ backgroundColor: 'var(--sca-border)' }}>
                <div
                  className="h-3 rounded-full transition-all"
                  style={{
                    width: `${hour.percent}%`,
                    backgroundColor: 'var(--sca-teal)'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Zone Breakdown
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {zones.map((zone, idx) => (
            <div
              key={idx}
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
                    {zone.name}
                  </div>
                  <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                    Avg. dwell: {zone.avgDwell}
                  </div>
                </div>
                <Activity size={18} style={{ color: 'var(--sca-teal)' }} />
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-2xl font-bold" style={{ color: 'var(--sca-ink)' }}>
                  {zone.current}
                </span>
                <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                  of {zone.capacity}
                </span>
              </div>
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--sca-border)' }}>
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${(zone.current / zone.capacity) * 100}%`,
                    backgroundColor: 'var(--sca-teal)'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StoreOccupancy;
